/*
|--------------------------------------------------------------------------
| File: axiosInstance.js
| Description: Axios instance setup with default headers, token injection,
|              and global response handling with toast/alert feedback.
| Author: Sayed Ahmed
|--------------------------------------------------------------------------
*/

import axios from "axios";
import toast from "../utils/toastService";
import alert from "../utils/alertService";

// Base API URL from environment variable
const baseUrl = process.env.REACT_APP_VERSEL_API;

const api = axios.create({
  baseURL: `${baseUrl}/api`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Language": localStorage.getItem("langSlug") ?? 'en',
  },
});

// Flag to prevent duplicate 401 handling
let isUnauthorizedHandled = false;

// Throttle alerts/toasts: only one feedback per 5 seconds for statuses 200, 401, 403, 404, 500
const FEEDBACK_DEBOUNCE_MS = 5000;
let lastFeedbackTime = 0;

function shouldShowFeedback() {
  const now = Date.now();
  if (now - lastFeedbackTime >= FEEDBACK_DEBOUNCE_MS) {
    lastFeedbackTime = now;
    return true;
  }
  return false;
}

/**
 * REQUEST INTERCEPTOR
 * Attaches Bearer token from localStorage to outgoing requests.
 */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * RESPONSE INTERCEPTOR
 * Handles success and error responses globally.
 */
api.interceptors.response.use(
  (response) => {
    const { success, message, status_code } = response.data;

    // Show toast for success messages (excluding empty 204 response)
    if (success && message && status_code !== 204 && shouldShowFeedback()) {
      toast.success(message);
    }

    return response;
  },

  (error) => {
    const response = error.response;

    // Handle network or server unreachable
    if (!response) {
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    const { success, message, errors } = response.data || {};
    const status = response.status;

    // Custom app-level failure handling
    if (success === false) {
      if (status === 422 && errors) {
        toast.errorList(errors); // Field-level validation
      } else {
        toast.error(message || "Something went wrong.");
      }
      return Promise.reject(response);
    }

    // Common HTTP status handling
    switch (status) {
      case 401:
        if (!isUnauthorizedHandled) {
          isUnauthorizedHandled = true;

          // reset after a short delay to allow handling again later
          setTimeout(() => {
            isUnauthorizedHandled = false;
          }, 3000); // 3 seconds

          if (window.location.pathname !== "/login-page") {
            if (shouldShowFeedback()) toast.error("Unauthorized. Please log in.");
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            localStorage.removeItem("expire_date");
            window.location.pathname = "/login-page";
          } else {
            if (shouldShowFeedback()) toast.error(message || "Invalid credentials");
          }
        }
        break;

      case 403:
        if (shouldShowFeedback()) alert.error("Access denied.");
        break;

      case 404:
        if (shouldShowFeedback()) alert.error("Resource not found.");
        break;

      case 500:
        if (shouldShowFeedback()) alert.error("Internal server error.");
        break;

      default:
        toast.error(message || "An unexpected error occurred.");
        break;
    }

    return Promise.reject(response);
  }
);

export default api;
